import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  CloudIcon,
  ServerIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  BellAlertIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const DocFlowAIPage: React.FC = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="gradient-bg text-white relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              <DocumentTextIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">Product 3 of 4</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Turn Documents into <span className="text-blue-300">Intelligence</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
              Intelligent document processing that extracts, classifies, and tracks everything that matters in your contracts, receipts, and business documents. From VAT recovery to payment schedules to lease renewals—your data, your infrastructure, your control.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#demo"
                className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Request Demo</span>
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200"
              >
                <span>See How It Works</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What is DocFlow AI?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              DocFlow AI combines advanced OCR technology with AI-powered classification to automate your document workflows. Process receipts, invoices, contracts, and statements—extracting structured data, tracking critical dates, and storing everything in a searchable database.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-200"
            >
              <div className="text-4xl font-bold text-blue-600 mb-2">90%</div>
              <p className="text-gray-700 font-semibold">Reduction in manual data entry</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200"
            >
              <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
              <p className="text-gray-700 font-semibold">Automated deadline tracking</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200"
            >
              <div className="text-4xl font-bold text-purple-600 mb-2">Instant</div>
              <p className="text-gray-700 font-semibold">Search across all documents</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl border-2 border-orange-200"
            >
              <div className="text-4xl font-bold text-orange-600 mb-2">Flexible</div>
              <p className="text-gray-700 font-semibold">Private, cloud, or hybrid</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Deployment Flexibility Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Choose the Right Balance of Privacy and Cost
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Hybrid Deployment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-xl border-2 border-purple-500"
            >
              <div className="flex items-center space-x-3 mb-4">
                <ArrowsRightLeftIcon className="w-8 h-8 text-purple-600" />
                <h3 className="text-2xl font-bold text-gray-900">Hybrid Deployment</h3>
              </div>
              <div className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold mb-4">
                Recommended
              </div>
              <p className="text-gray-700 mb-6">
                Route sensitive contracts to private processing while using cost-effective cloud OCR for standard documents.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Best of both worlds</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Optimized costs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Complete control where it matters</span>
                </li>
              </ul>
            </motion.div>

            {/* Private Deployment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-xl border-2 border-green-500"
            >
              <div className="flex items-center space-x-3 mb-4">
                <ServerIcon className="w-8 h-8 text-green-600" />
                <h3 className="text-2xl font-bold text-gray-900">Private Deployment</h3>
              </div>
              <div className="mb-4 h-8"></div>
              <p className="text-gray-700 mb-6">
                Process everything on your infrastructure—complete data sovereignty for highly regulated industries.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">100% data control</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">No external dependencies</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Meet strictest compliance</span>
                </li>
              </ul>
            </motion.div>

            {/* Cloud Deployment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-xl border-2 border-blue-500"
            >
              <div className="flex items-center space-x-3 mb-4">
                <CloudIcon className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-900">Cloud Deployment</h3>
              </div>
              <div className="mb-4 h-8"></div>
              <p className="text-gray-700 mb-6">
                Fast, cost-effective processing for standard documents with lower sensitivity.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Quick setup</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Pay-as-you-go</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Minimal infrastructure</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Capabilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What DocFlow AI Does
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl"
            >
              <DocumentTextIcon className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">OCR & Data Extraction</h3>
              <p className="text-gray-700">
                Extract text, tables, and structured data from PDFs, images, and scanned documents. Support for receipts, invoices, contracts, bank statements, and more.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl"
            >
              <svg className="w-12 h-12 text-purple-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Classification</h3>
              <p className="text-gray-700">
                Automatic categorization and intelligent extraction of key information—vendor names, dates, amounts, payment terms, obligations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl"
            >
              <ChartBarIcon className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Contract Intelligence</h3>
              <p className="text-gray-700">
                Beyond simple OCR—understand payment schedules, track milestones, monitor retention, identify risks, and flag critical deadlines.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl"
            >
              <MagnifyingGlassIcon className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Searchable Database</h3>
              <p className="text-gray-700">
                Store structured data for instant retrieval. Query your entire document portfolio using natural language with DB Talker integration.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl"
            >
              <CheckCircleIcon className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Human Verification</h3>
              <p className="text-gray-700">
                Review queue for low-confidence extractions with quick approval workflows and complete audit trails.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl"
            >
              <BellAlertIcon className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Alerts</h3>
              <p className="text-gray-700">
                Automated notifications for payment due dates, contract renewals, insurance expirations, and compliance deadlines.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases by Industry Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Use Cases by Industry
            </h2>
          </div>

          <div className="space-y-8">
            {/* Real Estate */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Real Estate & Property Management</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Lease Management</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span className="text-gray-700">Track rent escalations and payment schedules</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span className="text-gray-700">Automated renewal alerts (30/60/90 days)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span className="text-gray-700">Monitor CAM reconciliation obligations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span className="text-gray-700">Portfolio-wide lease analytics</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-blue-900 mb-2">Example Query:</p>
                    <p className="text-gray-700 italic">"Show me all leases expiring in Q2 2025"</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-green-900 mb-2">ROI:</p>
                    <p className="text-gray-700">300+ hours saved annually, zero missed renewals</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Construction */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Construction & Engineering</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Contract & Payment Management</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span className="text-gray-700">Extract payment terms and retention amounts</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span className="text-gray-700">Track milestone payments automatically</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span className="text-gray-700">Process AIA payment applications in minutes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span className="text-gray-700">Monitor subcontractor compliance</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <div className="bg-orange-50 p-4 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-orange-900 mb-2">Example Query:</p>
                    <p className="text-gray-700 italic">"Show projects with retention over $100K"</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-green-900 mb-2">ROI:</p>
                    <p className="text-gray-700">200+ hours saved monthly, 90% reduction in payment disputes</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Finance */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Finance & Accounting</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Expense & AP Automation</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">Automatic VAT classification and calculation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">Invoice data extraction and validation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">Expense categorization and reporting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-700">Integration with accounting systems</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <div className="bg-green-50 p-4 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-green-900 mb-2">Example Query:</p>
                    <p className="text-gray-700 italic">"Total VAT recoverable this quarter?"</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-green-900 mb-2">ROI:</p>
                    <p className="text-gray-700">80% faster processing, 25% increase in VAT recovery</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Integration with QuickReach Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete Private AI Stack
            </p>
          </div>

          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-12 rounded-2xl">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 text-blue-400" />
                <h3 className="text-xl font-bold mb-2">DocFlow AI</h3>
                <p className="text-gray-300">Extract and classify documents</p>
              </div>
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="text-xl font-bold mb-2">DB Talker</h3>
                <p className="text-gray-300">Query everything in natural language</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="text-lg font-bold mb-4">Example Workflow:</h4>
              <ol className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">1</span>
                  <span>Upload construction contract</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">2</span>
                  <span>DocFlow AI extracts payment terms</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">3</span>
                  <span>DocFlow AI flags unfavorable retention clause</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">4</span>
                  <span>Ask DB Talker: "Show projects where final payment is due within 30 days"</span>
                </li>
              </ol>
            </div>

            <div className="mt-8 flex justify-center">
              <Link
                to="/db-talker"
                className="inline-flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                <span>Explore DB Talker</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">Four Simple Steps</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-4">1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Upload Documents</h3>
              <p className="text-gray-700">
                Drag-and-drop or batch upload. Support for PDF, images, CSV, Excel. Single documents or bulk processing.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-4">2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Automatic Processing</h3>
              <p className="text-gray-700">
                Smart routing determines OCR path. AI extracts data and classifies content. Confidence scoring flags items for review.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-4">3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Human Verification</h3>
              <p className="text-gray-700">
                Review low-confidence extractions in minutes. Quick approve/correct interface. Complete audit trail maintained.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <div className="bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-4">4</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Search & Query</h3>
              <p className="text-gray-700">
                Instant full-text search across all documents. Natural language queries via DB Talker. Export to accounting/ERP systems.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo" className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Document Processing?
          </h2>
          <p className="text-xl mb-8">
            See DocFlow AI in action with your actual documents.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Request a Demo</span>
            </Link>
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all"
            >
              <span>Contact Sales</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocFlowAIPage;

